import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useCompleteBookingPayment from "../../hooks/useCompleteBookingPayment";
import useUserOrgMemberships from "../../hooks/useUserOrgMemberships";
import { useTrips } from "../../context/TripsContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Airplane,
  CaretLeft,
  Check,
  ShieldCheck,
  X,
  LightbulbFilament,
  Buildings,
  HourglassMedium,
  Money,
} from "@phosphor-icons/react";
import { HashLoader } from "react-spinners";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { formatAsMoney } from "../../helpers/numbers";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import useLivenessCheck from "../../hooks/useLivenessCheck";
import useCreateLivenessSession from "../../hooks/useCreateLivenessSession";
import BookingRecoveryNotice from "../../components/BookingRecoveryNotice";

const SCHEMA = yup.object().shape({
  accepted: yup.bool().isTrue("You have to accept the terms and conditions"),
});

const HIGH_VALUE = 70_000;

const PayLater = () => {
  const { flightData, setBooking } = useTrips();
  const { primaryOrg, isLoading: loadingOrg } = useUserOrgMemberships();

  const { state: params } = useLocation();

  const { user, refetch } = useAuth();

  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  const [bookingError, setBookingError] = useState(false);
  const [booked, setBooked] = useState(false);
  const [pendingOrgApproval, setPendingOrgApproval] = useState<{ orgName: string } | null>(null);

  const {
    confirmBookingPayment,
    isConfirmingPaymentError,
    confirmingBookingPayment,
    confirmBookingPaymentError,
  } = useCompleteBookingPayment({
    onError() {
      setBookingError(true);
    },
    onSuccess(data) {
      const payload = (data as any)?.data;
      if (payload?.status === "pending_org_approval") {
        setBooking(false);
        setPendingOrgApproval({ orgName: payload.org_name ?? primaryOrg?.organizationName ?? "your organisation" });
        return;
      }
      refetch();
      setBooked(true);
      setBooking(false);
      toast.success(data.message);
    },
  });

  const [showLivenessCheck, setShowLivenessCheck] = useState(false);

  const { createSession, isCreatingSession, sessionError } =
    useCreateLivenessSession();

  const [livenessSession, setLivenessSession] = useState<{
    sessionId: string;
    sessionToken: string;
  } | null>(null);

  const liveness = useLivenessCheck({
    sessionId: livenessSession?.sessionId ?? "",
    sessionToken: livenessSession?.sessionToken ?? "",
    onSuccess: () => {
      setShowLivenessCheck(false);
      setConfirming(true);
      window.scrollTo({ top: 0, left: 0 });
    },
    onFailure: (err: any) => {
      console.error("Liveness check failed", err);
      toast.error("Liveness verification failed. Please try again.");
    },
  });

  const handleStartLiveness = async () => {
    try {
      const newSession = await createSession();
      setLivenessSession(newSession);
      setTimeout(() => liveness.start(), 0);
    } catch {
      // sessionError is set by the hook and displayed in the modal
    }
  };

  const minimumDownPayment = Number(
    params?.paymentData?.payment_data?.minimum_down_payment ??
      params?.paymentData?.payment_data?.initial_payment_amount ??
      0
  );

  const ticketPrice = Number(
    params?.paymentData?.payment_data?.loan_amount ??
      params?.paymentData?.payment_data?.total_amount_payable ??
      0
  );

  const commissionFee = Number(
    params?.paymentData?.payment_data?.commission_fee ??
      params?.paymentData?.payment_data?.processing_fee ??
      0
  );

  const [downPaymentAmount, setDownPaymentAmount] = useState<number>(
    minimumDownPayment
  );

  const financedAmount = Math.max(ticketPrice - downPaymentAmount, 0);
  const isFullPayoff = ticketPrice > 0 && downPaymentAmount >= ticketPrice;

  // Total charged to the wallet upfront: commission + whatever down payment the user chose.
  const downPayment = commissionFee + downPaymentAmount;

  // A refresh drops the booking and the loan quote, which would otherwise render
  // ₦0 and confirm with an undefined booking id.
  const bookingId = flightData?.id;
  const optionCode = params?.paymentData?.option?.code;
  const loanId = params?.paymentData?.payment_data?.loan_id;
  const canPay =
    !!bookingId &&
    !!optionCode &&
    !!loanId &&
    Number.isFinite(ticketPrice) &&
    ticketPrice > 0;

  if (!canPay) {
    return (
      <div className="flex justify-center px-5 py-20">
        <div className="w-full max-w-xl">
          <BookingRecoveryNotice showPaymentOptions={!!bookingId} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {confirmingBookingPayment && (
        <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      {confirming && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-visaro-primary-700 rounded-full p-8 text-white">
              <Airplane size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Are you sure?</div>
              <div className="text-lg">
                You are about to be charged{" "}
                <b className="font-semibold">₦ {formatAsMoney(downPayment)} </b>
                from your wallet balance
                {isFullPayoff
                  ? " to pay for this ticket in full."
                  : " as an upfront payment for this loan."}
              </div>
            </div>
            <div className="flex justify-between w-full">
              <button
                onClick={() => setConfirming(false)}
                type="button"
                className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={() => {
                  if (!bookingId) return;
                  setConfirming(false);
                  confirmBookingPayment({
                    booking_id: bookingId,
                    payment_option_id: params?.paymentData?.option?.code,
                    loan_id: params?.paymentData?.payment_data?.loan_id,
                    payment_installment_id:
                      params?.paymentData?.payment_data?.payment_installment_id,
                    down_payment_amount: downPaymentAmount,
                  });
                }}
                disabled={!bookingId}
                className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Pay
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {showLivenessCheck && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-0 left-0 w-full h-screen z-50 bg-gray-500/60 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 flex flex-col items-center gap-5"
            >
              <div className="p-5 bg-gray-50 rounded-full">
                <ShieldCheck size={40} className="text-deepBlue" />
              </div>
              <h2 className="text-xl font-semibold text-center">
                Liveness Verification
              </h2>
              <p className="text-gray-700 text-center">
                For transactions above <b>₦100,000</b>, we require a quick
                liveness verification to ensure the security of your funds.
              </p>
              {(sessionError || liveness.error) && (
                <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-2 w-full text-center">
                  {sessionError || liveness.error}
                </div>
              )}
              <button
                className="bg-deepBlue text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isCreatingSession || liveness.status === "running"}
                onClick={handleStartLiveness}
              >
                {isCreatingSession
                  ? "Preparing..."
                  : liveness.status === "running"
                  ? "Starting..."
                  : "Start Liveness Check"}
              </button>
              <button
                className="mt-3 border border-gray-400 px-6 py-3 rounded-lg"
                onClick={() => setShowLivenessCheck(false)}
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {isConfirmingPaymentError && bookingError && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-red-500 rounded-full p-8 text-white">
              <X size={36} />
            </div>
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="text-3xl font-medium">Booking Failed!</div>
              <div className="w-full line-clamp-6">
                {confirmBookingPaymentError?.message.toString()}
              </div>
            </div>
            <button
              onClick={() => setBookingError(false)}
              type="button"
              className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
            >
              <span>Cancel</span>
            </button>
          </div>
        </motion.div>
      )}
      {booked && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-green-500 rounded-full p-8 text-white">
              <Check size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Loan Request Sent!</div>
              <div>
                Congratulations! Your loan request has been submitted successfully.{" "}
                {primaryOrg ? (
                  <>
                    Your request is pending approval from{" "}
                    <strong>{primaryOrg.organizationName}</strong>. Once approved,
                    your ticket will be issued and the repayment will be deducted
                    from your payroll.
                  </>
                ) : (
                  <>
                    Your flight ticket will be booked and sent to you after your
                    request has been approved.
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                navigate("/home");
                setBooked(false);
              }}
              type="button"
              className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
            >
              <span>Go to Dashboard</span>
            </button>
          </div>
        </motion.div>
      )}

      {pendingOrgApproval && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "tween", ease: "anticipate" }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-amber-400 rounded-full p-8 text-white">
              <HourglassMedium size={36} weight="fill" />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Awaiting Organisation Approval</div>
              <div className="text-gray-600 max-w-md">
                Your loan request has been submitted to{" "}
                <strong>{pendingOrgApproval.orgName}</strong> for approval. You
                will be notified by email once it has been reviewed. No charges
                have been applied yet.
              </div>
            </div>
            <button
              onClick={() => {
                navigate("/home");
                setPendingOrgApproval(null);
              }}
              type="button"
              className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
            >
              <span>Go to Dashboard</span>
            </button>
          </div>
        </motion.div>
      )}
      <Formik
        validationSchema={SCHEMA}
        initialValues={{
          accepted: false,
        }}
        onSubmit={() => {
          // Wallet existence check
          if (!user?.wallet) {
            toast.error("You need an active Visaro wallet to pay in installments. Please activate your wallet first.");
            return;
          }
          // Down payment bounds check
          if (downPaymentAmount < minimumDownPayment) {
            toast.error(
              `Down payment must be at least ₦${formatAsMoney(minimumDownPayment)}.`
            );
            return;
          }
          if (ticketPrice > 0 && downPaymentAmount > ticketPrice) {
            toast.error(
              `Down payment cannot exceed the ticket price of ₦${formatAsMoney(ticketPrice)}.`
            );
            return;
          }
          // Balance sufficiency check
          const balance = user.wallet.available_balance || 0;
          if (balance < downPayment) {
            const deficit = downPayment - balance;
            toast.error(
              `Insufficient wallet balance for the down payment. You need an additional ₦${formatAsMoney(deficit)} to complete this transaction.`
            );
            return;
          }

          if (downPayment > HIGH_VALUE && liveness.status !== "success") {
            liveness.reset();
            setShowLivenessCheck(true); // Show liveness modal
          } else {
            setConfirming(true); // Continue as normal
            window.scrollTo({ top: 0, left: 0 });
          }
        }}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="relative w-full flex items-center justify-center gap-10 px-5 py-20 flex-col">
              <div className="flex flex-col gap-5 max-w-4xl w-full items-start">
                <div className="flex items-center justify-between w-full">
                  <div className="font-semibold text-3xl">Fly Now Pay Later (FNPL)</div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 rounded-xl border border-gray-100">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">Powered by</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-gray-700">Credicorp</span>
                      <img src="/credicorp.png" alt="CrediCorp" className="h-7 w-auto" />
                    </div>
                  </div>
                </div>
                <div className="text-gray-500">You'll be charged upfront</div>
                <div className="text-3xl font-semibold">
                  ₦ {formatAsMoney(downPayment)}
                </div>
                <div className="text-gray-700">
                  <b>₦ {formatAsMoney(ticketPrice)} </b>
                  total flight cost.
                </div>
                
                {/* Org payroll deduction notice */}
                {loadingOrg ? (
                  <div className="w-full h-28 bg-deepBlue/10 rounded-xl animate-pulse" />
                ) : primaryOrg ? (
                  <div className="w-full bg-deepBlue rounded-xl p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <Buildings size={20} weight="fill" className="text-white" />
                      <span className="font-semibold text-white">Organisation Credit</span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-white/60 text-xs uppercase tracking-wide">Organisation</span>
                        <span className="font-semibold text-white">{primaryOrg.organizationName}</span>
                      </div>
                      {primaryOrg.creditLimit != null && (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-white/60 text-xs uppercase tracking-wide">Credit Limit</span>
                          <span className="font-semibold text-white">₦ {formatAsMoney(primaryOrg.creditLimit)}</span>
                        </div>
                      )}
                      {primaryOrg.availableCredit != null && (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-white/60 text-xs uppercase tracking-wide">Available Credit</span>
                          <span className="font-semibold text-green-300">₦ {formatAsMoney(primaryOrg.availableCredit)}</span>
                        </div>
                      )}
                      {primaryOrg.usedCredit != null && (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-white/60 text-xs uppercase tracking-wide">Used Credit</span>
                          <span className="font-semibold text-white">₦ {formatAsMoney(primaryOrg.usedCredit)}</span>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-white/20 pt-3 flex items-start gap-2 text-sm text-white/80">
                      <Money size={16} weight="fill" className="text-white mt-0.5 shrink-0" />
                      <span>
                        Your loan repayment will be automatically deducted from your payroll by{" "}
                        <strong className="text-white">{primaryOrg.organizationName}</strong>. The
                        deduction will be reflected in your{" "}
                        <strong className="text-white">next payroll cycle</strong> after loan disbursement.
                        Your organisation admin must approve this request before your ticket is issued.
                      </span>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col mt-4 bg-blue-50/50 p-5 rounded-xl border border-blue-100">
                  <div className="flex items-start gap-4">
                    <div className="text-blue-500 mt-1">
                       <LightbulbFilament size={24} weight="fill" />
                    </div>
                    <div className="text-gray-700 leading-relaxed font-medium">
                      {params?.paymentData?.payment_data?.note || "Repayment schedule will be provided after loan approval by our disbursement partner."}
                    </div>
                  </div>
                </div>

                <div className="flex flex-row gap-10 mt-2">
                  <div className="flex flex-col gap-1">
                    <div className="font-medium text-gray-500">Commission Fee</div>
                    <div className="text-lg font-semibold">
                      ₦ {formatAsMoney(commissionFee)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-4 w-full max-w-xs">
                  <label htmlFor="downPaymentAmount" className="font-medium text-gray-500">
                    Down Payment
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      ₦
                    </span>
                    <input
                      id="downPaymentAmount"
                      type="number"
                      min={minimumDownPayment}
                      max={ticketPrice || undefined}
                      value={downPaymentAmount}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setDownPaymentAmount(Number.isNaN(value) ? 0 : value);
                      }}
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-xl font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-deepBlue/50"
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    Minimum down payment is{" "}
                    <b>₦ {formatAsMoney(minimumDownPayment)}</b>
                    {" "}({params?.paymentData?.payment_data?.initial_payment_percentage ?? 10}% of the ticket price). You may pay more to reduce the loan amount.
                  </div>
                  <div className="text-sm font-medium">
                    {isFullPayoff
                      ? "You're paying the full ticket price — no loan will be created."
                      : `₦ ${formatAsMoney(financedAmount)} will be financed as a loan.`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Field
                    type="checkbox"
                    name="accepted"
                    className="w-4 aspect-square accent-deepBlue"
                  />{" "}
                  <span>
                    By clicking ‘Continue’ , you agree to{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Terms and Condition
                    </Link>{" "}
                    &{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </div>
              </div>
              <div className="flex justify-between w-full max-w-4xl">
                <button
                  onClick={() => navigate(-1)}
                  type="button"
                  className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                >
                  <CaretLeft />
                  <span>Back</span>
                </button>
                <button
                  disabled={!isValid || !dirty}
                  type="submit"
                  className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
                >
                  {downPayment > HIGH_VALUE && liveness.status !== "success"
                    ? "Verify & Pay"
                    : "Pay"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PayLater;
